import { useGetProductPricesQuery, useSetProductPricesMutation } from '@codezo/graphql';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { useCurrencyStore } from '../../stores/currency';
import {
	buildTierGroups,
	HOUSE_LADDER,
	newTierDraft,
	rowToTierDraft,
	TierListEditor,
	type TierDraft,
	type TierGroup,
} from './product-price-tiers';

/**
 * What the admin asked to do. The modal always LOADS and SUBMITS every ladder —
 * `setProductPrices` replaces one whole ladder per call, so anything left out is
 * deleted — but it only SHOWS what was asked for. Editing one tier should not
 * present the other twelve.
 */
export type PricesEditorMode =
	| { kind: 'tier'; tierId: string }
	| { kind: 'new'; vetterEntityId?: string };

/**
 * Edits every catalogue price on a product — house and all suppliers together.
 *
 * Each tier names its own supplier, exactly as in the create flow. Splitting the
 * two (per-tier there, one ladder at a time here) meant opening a price and
 * finding no way to change or add a supplier without closing and re-filtering.
 *
 * `setProductPrices` still replaces ONE supplier's prices per call, so saving
 * groups the tiers by supplier and fires one call each. Two consequences shape
 * this component:
 *
 * It loads EVERY row, `includeInactive: true` included. A row left out of the
 * submission is deleted, so an editor that loaded only the active rows, or only
 * one supplier's, would silently destroy the rest.
 *
 * And a supplier whose last tier is removed here needs an explicit empty
 * submission — with no tiers left it forms no group, so without this its rows
 * would simply survive the save that was meant to clear them.
 */
export function SetProductPricesModal({
	open,
	onOpenChange,
	productId,
	mode,
	onSuccess,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	productId: string;
	mode: PricesEditorMode;
	onSuccess: () => void;
}) {
	const { selectedCurrency } = useCurrencyStore();
	const [tiers, setTiers] = useState<TierDraft[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [pendingClear, setPendingClear] = useState<TierGroup[] | null>(null);
	/** The key of the blank tier staged in `new` mode, so it can be shown alone. */
	const [newKey, setNewKey] = useState<string | null>(null);

	const { data, loading: loadingRows } = useGetProductPricesQuery({
		// No vetterEntityId: every ladder on the product, so that saving can
		// rewrite them all without deleting the ones that were never on screen.
		variables: { input: { productId, includeInactive: true }, currency: selectedCurrency },
		skip: !open,
		fetchPolicy: 'no-cache',
	});

	const [setProductPrices, { loading: saving }] = useSetProductPricesMutation();

	/** Ladders that existed when the modal opened, by picker value. */
	const initialLadders = useMemo(() => {
		const names = new Map<string, string>();
		for (const row of data?.getProductPrices ?? []) {
			names.set(row.vetterEntityId ?? HOUSE_LADDER, row.vetterEntity?.name ?? 'No supplier');
		}
		return names;
	}, [data]);

	useEffect(() => {
		if (!open || !data) return;
		const sorted = [...data.getProductPrices].sort(
			(a, b) =>
				// House first, then suppliers by name, then by volume within each.
				Number(a.vetterEntityId != null) - Number(b.vetterEntityId != null) ||
				(a.vetterEntity?.name ?? '').localeCompare(b.vetterEntity?.name ?? '') ||
				a.quantity - b.quantity
		);
		const loaded = sorted.map((row) => rowToTierDraft(row));
		if (mode.kind === 'new') {
			const draft = newTierDraft(
				selectedCurrency,
				`new-${Date.now()}`,
				mode.vetterEntityId ?? HOUSE_LADDER
			);
			setNewKey(draft.key);
			setTiers([...loaded, draft]);
		} else {
			setNewKey(null);
			setTiers(loaded);
		}
		setError(null);
		// `mode` is a fresh object each render; its identity must not re-run this
		// and wipe what the admin has typed.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, data]);

	/** The one tier this mode is about. */
	const visibleTiers = tiers.filter(
		(t) => t.key === (mode.kind === 'new' ? newKey : mode.tierId)
	);

	/**
	 * Folds an edited subset back into the full set, preserving order and
	 * dropping whatever the subset removed. The tiers not on screen are
	 * untouched, and they still get submitted — leaving one out deletes it.
	 */
	const mergeVisible = (all: TierDraft[], edited: TierDraft[]): TierDraft[] => {
		const byKey = new Map(edited.map((t) => [t.key, t]));
		const visibleKeys = new Set(visibleTiers.map((t) => t.key));
		return all.flatMap((t) => {
			if (!visibleKeys.has(t.key)) return [t];
			const next = byKey.get(t.key);
			return next ? [next] : [];
		});
	};

	const title = mode.kind === 'new' ? 'Add price' : 'Edit price';

	/** Groups to submit, including an empty one per ladder the admin emptied. */
	const buildSubmission = (): TierGroup[] | null => {
		const built = buildTierGroups(tiers);
		if (!built.ok) {
			setError(built.error);
			return null;
		}
		setError(null);
		const kept = new Set(built.groups.map((g) => g.vetterEntityId ?? HOUSE_LADDER));
		const cleared: TierGroup[] = [...initialLadders.keys()]
			.filter((key) => !kept.has(key))
			.map((key) => ({
				vetterEntityId: key === HOUSE_LADDER ? null : key,
				tiers: [],
			}));
		return [...built.groups, ...cleared];
	};

	const save = async (groups: TierGroup[]) => {
		try {
			// Sequential: each call is a whole-ladder replace, and one failing should
			// not race the others.
			for (const group of groups) {
				await setProductPrices({
					variables: {
						input: {
							productId,
							vetterEntityId: group.vetterEntityId,
							tiers: group.tiers,
						},
					},
				});
			}
			toast.success('Prices saved');
			onOpenChange(false);
			onSuccess();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to save prices');
		}
	};

	const handleSubmit = () => {
		const groups = buildSubmission();
		if (!groups) return;
		// Emptying a supplier is a legitimate thing to want and an easy thing to
		// reach by accident, so it is confirmed like the delete it is.
		if (groups.some((g) => g.tiers.length === 0)) {
			setPendingClear(groups);
			return;
		}
		void save(groups);
	};

	const clearedNames = (pendingClear ?? [])
		.filter((g) => g.tiers.length === 0)
		.map((g) => initialLadders.get(g.vetterEntityId ?? HOUSE_LADDER) ?? 'No supplier');

	return (
		<>
			<Dialog open={open} onOpenChange={onOpenChange}>
				<DialogContent className="sm:max-w-2xl">
					<DialogHeader>
						<DialogTitle>{title}</DialogTitle>
					</DialogHeader>
					<DialogForm onSubmit={handleSubmit}>
						<DialogBody>
							<p className="mb-3 text-sm text-text-3">
								A price belongs to a supplier, or to no supplier — the fallback used
								when a supplier has no price of its own. Other prices on this
								product are left as they are.
							</p>

							{loadingRows ? (
								<div className="h-20 animate-pulse rounded bg-surface-fill" />
							) : (
								<TierListEditor
									tiers={visibleTiers}
									/* Edits land in the full set, which is what gets
									   submitted — the hidden tiers must survive the save. */
									onChange={(next) => setTiers(mergeVisible(tiers, next))}
									currency={selectedCurrency}
									supplierPicker
									allowAdd={false}
									emptyNote="This price has been removed. Save to confirm."
								/>
							)}

							{error && <p className="mt-3 text-sm text-destructive">{error}</p>}
						</DialogBody>
						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => onOpenChange(false)}
							>
								Cancel
							</Button>
							<Button type="submit" disabled={saving || loadingRows}>
								{saving ? 'Saving…' : 'Save prices'}
							</Button>
						</DialogFooter>
					</DialogForm>
				</DialogContent>
			</Dialog>

			<ConfirmDialog
				open={!!pendingClear}
				onOpenChange={(o) => !o && setPendingClear(null)}
				title={
					clearedNames.length === 1
						? `Remove all ${clearedNames[0]} prices?`
						: 'Remove prices for these suppliers?'
				}
				description={
					clearedNames.length === 1
						? `Every ${clearedNames[0]} price on this product will be deleted.`
						: `Every price will be deleted for: ${clearedNames.join(', ')}.`
				}
				confirmLabel="Remove"
				loading={saving}
				onConfirm={() => {
					const groups = pendingClear;
					setPendingClear(null);
					if (groups) void save(groups);
				}}
			/>
		</>
	);
}
